import React, { useEffect, useState } from 'react'
import { eachDayOfInterval, endOfDay, startOfDay, subDays, subYears } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useAtom } from 'jotai'
import { useTheme } from '@mui/material/styles'
import { formatNumber } from '../../../packages/utils/numberFormatters'
import { issueFiltersAtom } from '../../../atoms/issueAtom'
import { TimeLineChart } from '../../../components/TimeLineChart'
import { LineSeriesType } from '@mui/x-charts/models'
import { WarrantEntity } from 'types/WarrantEntity'

export const getListedWarrantsForDates = (
    warrants: Array<
        Pick<WarrantEntity, 'instrumentCreatedAt' | 'subscriptionPeriods' | 'unlisted' | 'publicationDate' | 'origins'>
    >,
    dates: Date[],
) => {
    const warrantsWithRelevantDate = warrants.map((w) => ({
        ...w,
        startDate: [w.publicationDate, ...(w.origins || []).map((o) => o.publicationDate)]
            .filter((d) => d !== null)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .pop(),
    }))
    return dates.map((d) => {
        return warrantsWithRelevantDate.filter(
            (w) =>
                w.startDate &&
                startOfDay(w.startDate) < d &&
                w.subscriptionPeriods.some((sp) => sp.subEndDate !== null && endOfDay(new Date(sp.subEndDate)) > d) &&
                !w.unlisted,
        ).length
    })
}

export const ListedWarrantsChart = ({
    daysCount,
    isLoading,
    warrants,
}: {
    daysCount: number
    isLoading: boolean
    warrants: WarrantEntity[] | null
}) => {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation()
    const theme = useTheme()
    const [filters] = useAtom(issueFiltersAtom)
    const [seriesData, setSeriesData] = useState<Array<number[]>>([])
    const [xAxisData, setXAxisData] = useState<Date[]>([])
    const [isCalculating, setIsCalculating] = useState(true)

    useEffect(() => {
        if (!warrants) return

        try {
            const startDates = [0, 1].map((yearCount) => {
                const endDate = subYears(new Date(), yearCount)
                const startDate = subDays(endDate, daysCount)

                return { endDate, startDate }
            })

            const chartData = startDates.map(({ startDate, endDate }) => {
                const days = eachDayOfInterval({ start: startDate, end: endDate })
                const data = getListedWarrantsForDates(warrants, days)

                return {
                    seriesData: data,
                    xAxisData: days,
                }
            })

            setSeriesData(chartData.map((c) => c.seriesData))
            setXAxisData(chartData[0].xAxisData)
        } catch (err) {
            console.error(err)
        } finally {
            setIsCalculating(false)
        }
    }, [daysCount, filters.dates.value, warrants])

    const series: LineSeriesType[] = seriesData.map((d, index) => ({
        type: 'line',
        data: d,
        color: index === 0 ? theme.palette.TO.main : theme.palette.TO.dark,
        label: index === 0 ? t('charts.legend.currentPeriod') : t('charts.legend.oneYearAgo'),
        ids: index === 0 ? 'current' : 'lastYear',
        valueFormatter: (v) =>
            v === null ? null : `${formatNumber(v, { decimals: 0, locale })} ${t('charts.units.st')}`,
    }))

    return <TimeLineChart series={series} isLoading={isLoading || isCalculating} xAxisData={xAxisData} />
}
